// Fast-forward to a step, for demos (mock only).
//
// Step 4 needs a verified account, a saved draft and a verified phone. Reaching
// it honestly costs three steps and two codes, which is fine once and hopeless
// when there are a dozen step-4 scenarios to walk through — so the launcher's
// Step 4 rows come through here instead.
//
// It seeds by calling the mock API the same way the real pages do, in the same
// order, rather than hand-writing a session object. That matters: a hand-built
// session can drift into a shape the real flow never produces, and then the
// scenario being demonstrated is not the one that ships. Everything here goes
// through the public seam, so if the flow's requirements change this breaks
// loudly instead of quietly seeding something impossible.
//
// It refuses to render outside the mock API.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import qs from "query-string";
import SubmissionLayout from "../common/SubmissionLayout";
import { getSubmissionFlowApi, isMockApi } from "../../../shared/api/submission";
import { MOCK_EMAIL_CODE, MOCK_PHONE_CODE } from "../../../shared/api/submission/mockSession";
import { setSubmissionAuth } from "../../../helpers/submissionAuth";
import { parseSubmissionParams, buildFlowSearch } from "../common/submissionParams";
import { resetMockBackend } from "../../../shared/api/submission/SubmissionFlowApi.mock";
import { SubmissionEntrySource } from "../common/persistedFlow";

interface Props extends DispatchProps {
  history: any;
  location: any;
}

// Someone recognisable enough to demo with, and obviously not a real person.
const SEED_ACTOR = {
  firstName: "Paola",
  lastName: "Hernandez",
  email: "paola.demo@theworkbook.com",
  password: "abc123",
  recaptchaToken: "mock-seed",
};

const SEED_NOTE =
  "I grew up two blocks from where this is set, and I have wanted this part since I read the book.";

// A placeholder headshot, drawn rather than shipped: a real photo of a real
// person in the repo would be a face nobody consented to putting in a demo, and
// a blank grey box makes the review card look broken exactly where its whole
// point is showing what casting sees. 816x1020 is the flow's export size.
const seedPhoto = (): Promise<Blob | undefined> =>
  new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 816;
      canvas.height = 1020;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(undefined);
        return;
      }
      ctx.fillStyle = "#dfe7f1";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // A head-and-shoulders silhouette, so the 4:5 framing reads as a portrait.
      ctx.fillStyle = "#b6c6da";
      ctx.beginPath();
      ctx.arc(408, 400, 165, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(408, 1080, 330, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#6b7c93";
      ctx.font = "bold 40px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(i18next.t("DEMO PHOTO"), 408, 940);

      if (canvas.toBlob) {
        canvas.toBlob((blob) => resolve(blob || undefined), "image/jpeg", 0.9);
        return;
      }
      resolve(undefined);
    } catch (e) {
      resolve(undefined);
    }
  });

const MockSeedPage: React.FC<Props> = ({ history, location, setEntry, persist }) => {
  const [status, setStatus] = useState(i18next.t("Setting up the demo…"));

  useEffect(() => {
    if (!isMockApi()) {
      history.replace("/submission/casting-calls");
      return;
    }

    const query = (qs.parse(location.search) || {}) as any;
    const to = (query.to as string) || "/submission/flow/step/4";
    const params = parseSubmissionParams(location.search);
    // Everything except this page's own instructions travels on to the target,
    // so one row can combine a seed with ?step4=b, ?ctry=US or
    // ?mockScenario=trialUsed and the target sees them as if they were typed.
    const forwarded: any = { ...query };
    delete forwarded.to;
    delete forwarded.note;
    const onward = `${to}${buildFlowSearch(
      qs.stringify(forwarded),
      params.bd,
      params.roleid
    )}`;

    const seed = async () => {
      const api = getSubmissionFlowApi();
      try {
        // A stale session from an earlier row would keep its own breakdown and
        // its own membership state, which is the opposite of what a scenario
        // link is for.
        resetMockBackend();

        setStatus(i18next.t("Creating the account…"));
        // The account's country comes from the geo guess at creation, and the
        // membership check reads the ACCOUNT, never the URL — so a seeded
        // ?ctry=US only reaches the entitlement answer if the guess is fetched
        // and passed on here, exactly as step 1 does it. Without this the
        // "US talent submits free" row silently seeded a Canadian.
        const geo = await api.getRequesterLocation();
        const start = await api.startSignup({
          ...SEED_ACTOR,
          breakdownId: params.bd || undefined,
          roleId: params.roleid || undefined,
          geoCountryCode: geo.countryCode || undefined,
          geoRegionCode: geo.regionCode,
          geoCityId: geo.cityId,
        });
        const verified = await api.verifyEmailCode(start.sessionId, MOCK_EMAIL_CODE);
        if (verified.jwt && verified.clientId) {
          setSubmissionAuth({ clientId: verified.clientId, jwt: verified.jwt });
        }

        // The store needs the refs too, or the target page renders without its
        // blue context header while the gate re-check catches up.
        if (params.bd) {
          setEntry({
            entrySource: "direct" as SubmissionEntrySource,
            breakdownId: params.bd,
            roleId: params.roleid || null,
          });
          persist();
        }

        // ?materials=none: step 2 as an actor arrives at it, nothing chosen yet (the existing
        // user's skeleton and preselection).
        if (query.materials === "none") {
          history.replace(onward);
          return;
        }

        setStatus(i18next.t("Saving the materials…"));
        const draft = await api.createDraft(params.bd || null, params.roleid || null);
        const bytes = await seedPhoto();
        const photo = await api.uploadMedia({
          kind: "photo",
          fileName: "headshot.jpg",
          sizeBytes: bytes ? bytes.size : 1024 * 300,
          mimeType: "image/jpeg",
          file: bytes,
        });
        // A resume or a note, either one: without the note ("(No notes)" on the review), a resume.
        const noNote = query.note === "none";
        const resume = noNote
          ? await api.uploadMedia({
              kind: "resume",
              fileName: "Paola-Hernandez-Resume.pdf",
              sizeBytes: 1024 * 80,
              mimeType: "application/pdf",
              file: new Blob(["%PDF-1.4"], { type: "application/pdf" }),
            })
          : null;
        await api.saveMaterials(draft.submissionId, {
          photoAssetId: photo.assetId,
          resumeAssetId: resume ? resume.assetId : undefined,
          ageAttested: true,
          noteToCastingDirector: noNote ? "" : SEED_NOTE,
        });

        setStatus(i18next.t("Verifying the phone…"));
        await api.sendPhoneCode({ phone: "+1 604 555 0134", countryCode: "CA" });
        await api.verifyPhoneCode(MOCK_PHONE_CODE);

        history.replace(onward);
      } catch (err) {
        // Say which step failed rather than bouncing to a page that looks
        // fine and behaves oddly.
        const message = err && (err as any).errorCode ? (err as any).errorCode : "unknown";
        setStatus(
          i18next.t("Couldn't set up the demo ({{reason}}). Walk it from the boost page.", {
            reason: message,
          })
        );
      }
    };

    seed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubmissionLayout>
      <Centered aria-busy="true">
        <Watermark>{i18next.t("MOCK SEED — demo shortcut")}</Watermark>
        <Message>{status}</Message>
        <Note>
          {i18next.t(
            "Skipping steps 1-3 with a demo account. Nothing here reaches a real backend."
          )}
        </Note>
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const Watermark = styled.div`
  display: inline-block;
  margin-bottom: 14px;
  padding: 6px 10px;
  border: 1px dashed #c7053f;
  border-radius: 6px;
  color: #c7053f;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const Message = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #122640;
`;

const Note = styled.div`
  font-size: 12px;
  color: #8a94a6;
  line-height: 18px;
  margin-top: 8px;
`;

interface DispatchProps {
  setEntry: (payload: {
    entrySource: SubmissionEntrySource;
    breakdownId: string | null;
    roleId: string | null;
  }) => void;
  persist: () => void;
}

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  setEntry: dispatch.submissionFlowModel.setEntry,
  persist: dispatch.submissionFlowModel.persist,
});

export default connect(null, mapDispatchToProps)(MockSeedPage);
